// AskTheDataChip — inline "Ask the data" companion for BIDashboardPage.
// Posts the current overview + question to /api/bi/ask and renders the
// narrative + optional chart (bar/line/pie).
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sparkles, Loader2, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const PIE_COLORS = ['#0ea5e9', '#f43f5e', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#22d3ee', '#e879f9'];

const SUGGESTIONS = [
  'Which incident category is most common?',
  'Show me the CAPA on-time rate',
  'What is the trend in incidents over time?',
  'Which risk cells have the most items?',
  'Summarize this window in one sentence',
];

function ResultChart({ chartType, chartData, chartTitle }) {
  if (!chartType || chartType === 'none' || !chartData || chartData.length === 0) return null;

  return (
    <div className="mt-3">
      {chartTitle ? (
        <div className="text-slate-300 text-xs mb-2">{chartTitle}</div>
      ) : null}
      <ResponsiveContainer width="100%" height={220}>
        {chartType === 'bar' ? (
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
            <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
            <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6 }} />
            <Bar dataKey="value" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
          </BarChart>
        ) : chartType === 'line' ? (
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
            <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
            <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6 }} />
            <Line type="monotone" dataKey="value" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        ) : (
          <PieChart>
            <Pie data={chartData} innerRadius={40} outerRadius={80} dataKey="value" nameKey="label" paddingAngle={2}>
              {chartData.map((entry, i) => (
                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
              ))}
            </Pie>
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6 }} />
          </PieChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

export default function AskTheDataChip({ overview, language = 'en' }) {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (qOverride) => {
    const q = (qOverride || question).trim();
    if (q.length < 3 || !overview) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post('/bi/ask', { question: q, overview, lang: language });
      setResult(r.data);
      if (!qOverride) setQuestion('');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'AI Insights failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <Button
          onClick={() => setOpen(true)}
          className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white shadow-lg"
          size="sm"
          data-testid="bi-ask-open-btn"
          title="Ask an analyst question about this data"
        >
          <Sparkles className="w-4 h-4 mr-2" /> Ask the data
        </Button>
      )}
      {open && (
        <Card className="bg-slate-900/80 border-violet-800/40" data-testid="bi-ask-panel">
          <CardHeader className="flex-row items-start justify-between space-y-0">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-300" />
              Ask the data
              <span className="text-[10px] text-slate-500 font-normal ml-1">
                (based on your current window)
              </span>
            </CardTitle>
            <button
              type="button"
              onClick={() => { setOpen(false); setResult(null); setQuestion(''); }}
              className="text-slate-500 hover:text-slate-200"
              aria-label="Close"
              data-testid="bi-ask-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <Input
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submit()}
                placeholder="e.g. Which category leads incidents this quarter?"
                className="bg-slate-950 border-slate-800"
                disabled={loading}
                data-testid="bi-ask-input"
              />
              <Button
                onClick={() => submit()}
                disabled={loading || question.trim().length < 3}
                className="bg-violet-600 hover:bg-violet-500"
                data-testid="bi-ask-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>

            {!result && !loading && (
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => submit(s)}
                    className="text-[11px] px-2 py-1 rounded-full border border-slate-700 text-slate-400 hover:text-slate-100 hover:border-violet-500/50 transition-colors"
                    data-testid="bi-ask-suggestion"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {loading && (
              <div className="flex items-center gap-2 text-slate-400 text-sm py-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Analysing your data…
              </div>
            )}

            {result && (
              <div className="border-t border-slate-800 pt-3" data-testid="bi-ask-result">
                <div
                  dir={result.lang === 'ar' ? 'rtl' : 'ltr'}
                  className="text-slate-200 text-sm leading-relaxed whitespace-pre-line"
                >
                  {result.answer}
                </div>
                <ResultChart
                  chartType={result.chart_type}
                  chartData={result.chart_data}
                  chartTitle={result.chart_title}
                />
                <p className="text-[10px] text-slate-500 mt-2 italic">
                  AI-generated summary based on your current window · powered by GPT-5.2
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
}
